import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Textarea from '@cloudscape-design/components/textarea';
import Alert from '@cloudscape-design/components/alert';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Spinner from '@cloudscape-design/components/spinner';
import { getFilters, updateFilters } from '../../api/tickets';
import { FiltersSummary } from './ConfigSummaries';

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
}

export function FiltersSettings() {
  const [filters, setFiltersObj] = useState<Record<string, unknown> | null>(null);
  const [text, setText] = useState('');
  const [lastModified, setLastModified] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFilters();
      setFiltersObj(res.filters);
      setText(JSON.stringify(res.filters, null, 2));
      setLastModified(res.lastModified);
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to load filters: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const parsed = JSON.parse(text);
      await updateFilters(parsed);
      setMessage({ type: 'success', text: 'Filters saved. The next sync will use them.' });
      load();
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: e.message?.includes('JSON') ? 'Invalid JSON format.' : `Failed to save: ${e.message}`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container
      header={
        <Header variant="h2" description="These filters determine which tickets are fetched from Tickety during a sync.">
          Ticket Filters
        </Header>
      }
    >
      <SpaceBetween size="m">
        {message && (
          <Alert type={message.type} dismissible onDismiss={() => setMessage(null)}>{message.text}</Alert>
        )}

        <Box variant="small" color="text-body-secondary">Last modified: {loading ? 'Loading…' : formatDate(lastModified)}</Box>

        {loading ? <Spinner /> : <FiltersSummary filters={filters} />}

        <ExpandableSection headerText="Edit filters (raw JSON)">
          <SpaceBetween size="s">
            <Textarea
              value={text}
              onChange={({ detail }) => setText(detail.value)}
              rows={12}
              disabled={loading}
              spellcheck={false}
            />
            <Button onClick={handleSave} loading={saving} disabled={loading}>Save filters</Button>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  );
}
